import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, describeApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CircleNotch, Copy, EnvelopeSimple, SealCheck } from "@phosphor-icons/react";

const OUTCOMES = [
  { key: "got_discount", label: "Got discount" },
  { key: "no_discount", label: "No discount" },
  { key: "switched_vendors", label: "Switched vendors" },
];

export function VendorNegotiationDialog({ open, onOpenChange, insight, threshold, testIdPrefix = "vendor-insights", onUpdated }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draftId, setDraftId] = useState("");
  const [subject, setSubject] = useState("");
  const [recipient, setRecipient] = useState("");
  const [body, setBody] = useState("");
  const [outcome, setOutcome] = useState("");

  useEffect(() => {
    if (!open || !insight) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.post("/vendor-insights/draft", {
          business_id: insight.business_id,
          vendor_key: insight.vendor_key,
          threshold,
        });
        if (cancelled) return;
        setDraftId(data.id || "");
        setSubject(data.subject || "");
        setRecipient(insight.vendor_email || "");
        setBody(data.body_text || "");
        setOutcome(insight.latest_negotiation?.outcome || "");
      } catch (err) {
        if (!cancelled) toast.error(describeApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, insight, threshold]);

  const copyDraft = async () => {
    await navigator.clipboard?.writeText(`${subject}\n\n${body}`);
    toast.success("Negotiation draft copied");
  };

  const sendDraft = async () => {
    if (!draftId) return;
    if (!recipient.trim()) return toast.error("Add a vendor email first");
    setSending(true);
    try {
      await api.post("/vendor-insights/send", {
        draft_id: draftId,
        recipient_email: recipient,
        subject,
        body_text: body,
      });
      toast.success("Vendor email sent via Resend");
      onUpdated?.();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setSending(false);
    }
  };

  const markOutcome = async (nextOutcome) => {
    if (!draftId) return;
    try {
      await api.patch(`/vendor-negotiations/${draftId}/outcome`, { outcome: nextOutcome });
      setOutcome(nextOutcome);
      toast.success("Negotiation outcome saved");
      onUpdated?.();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-white" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl" style={{ color: "var(--bk-text)" }}>
            {`Negotiate with ${insight?.vendor_name || "this vendor"}`}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center gap-2 text-sm" style={{ color: "var(--bk-text-2)" }} data-testid={`${testIdPrefix}-dialog-loading`}>
            <CircleNotch size={18} className="animate-spin" /> Drafting Rowan's email…
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm leading-relaxed" style={{ color: "var(--bk-text-2)" }}>
              Edit the draft, send it directly via Resend, or copy it for later. Once the vendor replies, track the outcome here.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="vendor@company.com" data-testid={`${testIdPrefix}-recipient-input`} className="rounded-md border px-3 py-2.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Preferred pricing request" data-testid={`${testIdPrefix}-subject-input`} className="rounded-md border px-3 py-2.5 text-sm outline-none sm:col-span-2" style={{ borderColor: "var(--bk-border)" }} />
            </div>

            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} data-testid={`${testIdPrefix}-body-input`} className="w-full rounded-2xl border px-4 py-3 text-sm outline-none resize-y" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />

            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="flex flex-wrap gap-2">
                {OUTCOMES.map((item) => (
                  <button key={item.key} onClick={() => markOutcome(item.key)} data-testid={`${testIdPrefix}-outcome-${item.key}`} className="px-3 py-2 rounded-full border text-xs font-semibold transition-colors" style={outcome === item.key ? { borderColor: "var(--bk-success)", background: "var(--bk-success-bg)", color: "var(--bk-success)" } : { borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                    <SealCheck size={13} weight="duotone" className="inline mr-1" /> {item.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                <button onClick={copyDraft} data-testid={`${testIdPrefix}-copy-button`} className="px-4 py-2.5 rounded-full border text-sm font-semibold inline-flex items-center gap-2" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                  <Copy size={15} weight="bold" /> Copy
                </button>
                <button onClick={sendDraft} disabled={sending} data-testid={`${testIdPrefix}-send-button`} className="bk-btn-primary px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                  {sending ? <><CircleNotch size={15} className="animate-spin" /> Sending…</> : <><EnvelopeSimple size={15} weight="bold" /> Send via Resend</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}